import { z } from "zod";
import { flowEventSchema, flowPatientOverviewSchema } from "./flows";

// TES-298 (spec §18) — simulate & observe for TEST patients.
//
// Simulate: POST /employee-api/v1/patients/:id/simulate-event fires a
// catalog event through the real TES-280 dispatcher (persisted DomainEvent,
// flow triggers, completesOn auto-complete) for a patient with isTest=true.
// Observe: GET /employee-api/v1/patients/:id/test-state is the combined
// read model — what the simulation caused.

export const simulateEventResponseSchema = z.object({
  event: z.string(),
  domainEventId: z.string(),
  // Human-readable facts about the domain state the simulation materialized
  // (fabricated rows + journeyState advances) before firing the event —
  // empty for event-layer-only events or when everything already existed.
  // Defaulted so pre-materialization responses still parse.
  materialized: z.array(z.string()).default([]),
});
export type SimulateEventResponse = z.infer<typeof simulateEventResponseSchema>;

export const testPatientDomainEventSchema = z.object({
  id: z.string(),
  eventKey: z.string(),
  source: z.string(),
  occurredAt: z.string(),
  dispatchedAt: z.string().nullable(),
});

export const testPatientScheduledNotificationSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  fireAt: z.string(),
  dedupeKey: z.string(),
});

export const testPatientChatDraftSchema = z.object({
  id: z.string(),
  attribution: z.string(),
  status: z.string(),
  body: z.string(),
  createdAt: z.string(),
});

// ---------------------------------------------------------------------------
// In-app QA controls (patient-api/v1/test-mode) — TEMPORARY internal-QA
// surface. Only reachable for isTest patients (service 404s otherwise, so to
// a real patient the routes are indistinguishable from not-mounted). Lets a
// test patient drive the journey from the app: start/restart any published
// flow, fire catalog events, and time-travel day-gated content.
// ---------------------------------------------------------------------------

export const TEST_FLOW_ASSIGNMENT_STATUSES = [
  "none",
  "active",
  "paused",
  "completed",
  "cancelled",
] as const;
export const testFlowAssignmentStatusSchema = z.enum(
  TEST_FLOW_ASSIGNMENT_STATUSES,
);

export const patientTestCatalogFlowSchema = z.object({
  key: z.string(),
  // Swedish flow name (QA surface — no locale resolution).
  name: z.string(),
  trigger: z.string(),
  kind: z.string(),
  assignmentStatus: testFlowAssignmentStatusSchema,
});
export type PatientTestCatalogFlow = z.infer<
  typeof patientTestCatalogFlowSchema
>;

export const patientTestCatalogSchema = z.object({
  isTest: z.literal(true),
  events: z.array(flowEventSchema),
  flows: z.array(patientTestCatalogFlowSchema),
});
export type PatientTestCatalog = z.infer<typeof patientTestCatalogSchema>;

export const startTestFlowRequestSchema = z.object({
  flowKey: z.string().min(1),
});
export type StartTestFlowRequest = z.infer<typeof startTestFlowRequestSchema>;

export const startTestFlowResponseSchema = z.object({
  assignmentId: z.string(),
  flowKey: z.string(),
  // True when an active/paused assignment was cancelled to restart the flow.
  restarted: z.boolean(),
});
export type StartTestFlowResponse = z.infer<typeof startTestFlowResponseSchema>;

export const advanceDaysRequestSchema = z.object({
  days: z.number().int().min(1).max(30).default(1),
  // TES-1232 — opt-in: also move her upcoming meetings (scheduled /
  // in_progress) back by the same days, so a test can hold a booked meeting
  // whose start has passed. Off by default, so every existing caller shifts
  // exactly what it did before.
  meetings: z.boolean().default(false),
});
export type AdvanceDaysRequest = z.infer<typeof advanceDaysRequestSchema>;

export const advanceDaysResponseSchema = z.object({
  days: z.number().int(),
  shiftedAssignments: z.number().int(),
  shiftedItems: z.number().int(),
  treatmentShifted: z.boolean(),
  cyclesShifted: z.number().int(),
  meetingsShifted: z.number().int(),
});
export type AdvanceDaysResponse = z.infer<typeof advanceDaysResponseSchema>;

export const testPatientStateSchema = z.object({
  patientId: z.string(),
  isTest: z.boolean(),
  journeyState: z.string(),
  // Most recent first, capped server-side.
  events: z.array(testPatientDomainEventSchema),
  // The same overview the admin flow tab renders (active flows + timeline).
  flows: flowPatientOverviewSchema,
  scheduledNotifications: z.array(testPatientScheduledNotificationSchema),
  chatDrafts: z.array(testPatientChatDraftSchema),
});
export type TestPatientState = z.infer<typeof testPatientStateSchema>;
